import { useState, useEffect } from "react";
import { Avatar, Button, Popover, Typography } from "antd";
import {
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { CREATE_jwel } from "../Config/Config";
import { ALL_GROUPS } from "./SidebarConst"; // adjust the path if needed

const { Text } = Typography;

/* ── local color helpers ── */
const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(255, 255, 255, ${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${isNaN(r) ? 255 : r}, ${isNaN(g) ? 255 : g}, ${isNaN(b) ? 255 : b}, ${alpha})`;
};

/* shades a hex toward black (negative percent) or white (positive).
   Sidebar accents are vivid/neon tones that fail contrast on white, so the
   title text uses a deepened version of the same hue. */
const shadeColor = (hex, percent) => {
  if (!hex) return "#1f1f1f";
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  let r = parseInt(h.slice(0, 2), 16);
  let g = parseInt(h.slice(2, 4), 16);
  let b = parseInt(h.slice(4, 6), 16);
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  r = Math.round((t - r) * p) + r;
  g = Math.round((t - g) * p) + g;
  b = Math.round((t - b) * p) + b;
  const toHex = (v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/* ── route → { module, screen, color, icon } lookup, built once from
   SidebarConst so header titles always match the sidebar ── */
const buildRouteMap = () => {
  const map = {};
  ALL_GROUPS.forEach((group) => {
    const add = (item) => {
      if (item?.path && !map[item.path]) {
        map[item.path] = {
          module: group.label,
          screen: item.label,
          color: group.color,
          icon: group.icon,
        };
      }
    };
    (group.subGroups || []).forEach((sg) => (sg.items || []).forEach(add));
    (group.moduleReports || []).forEach((sg) => (sg.items || []).forEach(add));
    (group.items || []).forEach(add);
  });
  // routes that are not in the sidebar lists
  map["/dashboard"] = {
    module: null,
    screen: "Dashboard",
    color: "#3b82f6",
    icon: <HomeOutlined />,
  };
  return map;
};
const ROUTE_MAP = buildRouteMap();

const Header = ({ collapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const [isMobile, setIsMobile] = useState(false);
  const [userName, setUserName] = useState("");
  const [singleImage, setSingleImage] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const loginUsername = localStorage.getItem("UName") || "";

  const userAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=FIRM_CONFIGURE`,
        { headers: { tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserName(data[0]?.FIRMNAME);
        setSingleImage(data[0]?.EPASS2);
      }
    } catch (error) {
      console.error("Error fetching firm details:", error);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    userAPI();
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("tenantName");
    localStorage.clear();
    navigate("/");
    window.location.reload(); // ensure fresh login state
  };

  /* ── current screen title ── */
  const current = ROUTE_MAP[path];
  const moduleName = current?.module ? current.module.toUpperCase() : null;
  const screenName = current?.screen ? current.screen.toUpperCase() : null;
  const moduleColor = current?.color || "#64748b";
  const moduleIcon = current?.icon || <HomeOutlined />;
  // deepened "ink" version of the module color for legible text on white
  const moduleTextColor = shadeColor(moduleColor, -42);

  /* ── profile popover card: photo left, firm / username / logout right ── */
  const profileCard = (
    <div
      style={{
        width: isMobile ? 300 : 420,
        display: "flex",
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      {/* left column — firm logo */}
      <div
        style={{
          width: isMobile ? 96 : 130,
          flexShrink: 0,
          background: "linear-gradient(180deg, #eef0fb 0%, #e3e7f7 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Avatar
          size={isMobile ? 64 : 80}
          src={singleImage}
          icon={<UserOutlined />}
          style={{
            border: "2px solid #fff",
            boxShadow: "0 2px 8px rgba(15,36,96,0.25)",
          }}
        />
      </div>

      {/* right column — firm name / username / logout, stacked */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* row 1 — firm name */}
        <div style={{ padding: "14px 16px 12px", borderBottom: "1px solid #eef0f4" }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              color: "#9aa4c4",
              marginBottom: 3,
            }}
          >
            Firm
          </div>
          <Text
            strong
            ellipsis={{ tooltip: userName }}
            style={{ fontSize: 14.5, color: "#0f2460", display: "block" }}
          >
            {userName || "—"}
          </Text>
        </div>

        {/* row 2 — username */}
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #eef0f4",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <UserOutlined style={{ color: "#6b7ab8", fontSize: 13, flexShrink: 0 }} />
          <Text
            ellipsis={{ tooltip: loginUsername }}
            style={{ fontSize: 13, fontWeight: 600, color: "#2b2b2b", minWidth: 0 }}
          >
            {loginUsername || "—"}
          </Text>
        </div>

        {/* row 3 — logout */}
        <div
          onClick={handleLogout}
          style={{
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            marginTop: "auto",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#fdecea")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <LogoutOutlined style={{ color: "#e53935", fontSize: 13 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e53935" }}>
            Logout
          </span>
        </div>
      </div>
    </div>
  );

  const showExit = path === "/sale-return" || path === "/exchange-bill";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: isMobile ? "0 8px" : "0 16px",
        background: "#fff",
        color: "#000",
        height: isMobile ? "56px" : "64px",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
        gap: 8,
      }}
    >
      <style>{`
        /* module badge: breathing glow, clipped inside the icon circle */
        @keyframes hdrModulePulse {
          0%   { transform: scale(1);   opacity: 0.6; }
          70%  { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        .hdr-module-pulse {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          animation: hdrModulePulse 2s ease-out infinite;
          pointer-events: none;
        }
        @keyframes hdrTitleIn {
          from { opacity: 0; transform: translateY(-3px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .hdr-title-block { animation: hdrTitleIn 0.3s ease both; }

        /* profile popover card: no default antd padding, stronger shadow */
        .hdr-profile-popover .ant-popover-inner {
          padding: 0 !important;
          border-radius: 16px !important;
          overflow: hidden;
          box-shadow: 0 12px 32px rgba(15,36,96,0.22) !important;
        }
        .hdr-profile-popover .ant-popover-arrow {
          display: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .hdr-module-pulse, .hdr-title-block { animation: none !important; }
        }
      `}</style>

      {/* ── Left: sidebar toggle + module / screen title ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 6 : 12,
          minWidth: 0,
        }}
      >
     
        {screenName && (
          <div
            className="hdr-title-block"
            key={path} // re-trigger the entrance animation on every route change
            style={{ display: "flex", minWidth: 0 }}
          >
            {isMobile ? (
              /* mobile: compact — just the screen name */
              <Text
                strong
                title={screenName}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: moduleTextColor,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "48vw",
                }}
              >
                {screenName}
              </Text>
            ) : (
              <span
                title={moduleName ? `${moduleName} / ${screenName}` : screenName}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  width: "fit-content",
                  maxWidth: "100%",
                  padding: "3px 10px 3px 3px",
                  borderRadius: 20,
                  background: `linear-gradient(90deg, ${hexToRgba(moduleColor, 0.14)}, ${hexToRgba(moduleColor, 0.05)})`,
                  border: `1px solid ${hexToRgba(moduleColor, 0.4)}`,
                }}
              >
                {/* icon circle with the pulse glow clipped inside it */}
                <span
                  style={{
                    position: "relative",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: moduleColor,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  <span
                    className="hdr-module-pulse"
                    style={{ background: moduleColor }}
                  />
                  <span style={{ position: "relative", display: "flex" }}>
                    {moduleIcon}
                  </span>
                </span>

                {moduleName && (
                  <>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        color: moduleTextColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "clamp(90px, 14vw, 240px)",
                      }}
                    >
                      {moduleName}
                    </span>
                    <span style={{ color: moduleTextColor }}>/</span>
                  </>
                )}

                <Text
                  strong
                  style={{
                    fontSize: 11.5,
                    color: moduleTextColor,
                    fontWeight: 700,
                    letterSpacing: "0.2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    minWidth: 0,
                    maxWidth: "clamp(160px, 26vw, 380px)",
                  }}
                >
                  {screenName}
                </Text>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Right: Exit button (sale return / exchange bill) + avatar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 8 : 14,
          flexShrink: 0,
        }}
      >
        {showExit && (
          <Button
            style={{
              backgroundColor: "#BF092F",
              border: "1px solid #BF092F",
              color: "#ffffff",
              borderRadius: "8px",
              width: isMobile ? 70 : 100,
            }}
            onClick={() => navigate("/sale-invoice")}
          >
            Exit
          </Button>
        )}

        {/* Avatar + profile card — opens on hover or click */}
        <Popover
          content={profileCard}
          trigger={["click", "hover"]}
          open={profileOpen}
          onOpenChange={setProfileOpen}
          placement="bottomRight"
          overlayClassName="hdr-profile-popover"
          mouseEnterDelay={0.15}
        >
          <Avatar
            size={isMobile ? 34 : 50}
            src={singleImage}
            icon={<UserOutlined />}
            style={{ border: "2px solid grey", cursor: "pointer", flexShrink: 0 }}
          />
        </Popover>
      </div>
    </div>
  );
};

export default Header;